import fs from 'node:fs';
import path from 'node:path';
import chalk from 'chalk';
import Table from 'cli-table3';
import { createService, removeService } from 'unitup';
import { UnitupAdapter } from '../../runtime/unitup-adapter.js';
import { safeExec } from '../../security/exec.js';
import { isRootUser } from '../../security/path-validator.js';

export function printServiceHelp(): void {
  console.log(chalk.bold('\n⚙  Deployra Daemon Service Management\n'));
  console.log(`${chalk.yellow('Usage:')} deployra service <action>\n`);

  const table = new Table({
    head: [chalk.cyan('Action'), chalk.cyan('Description')],
  });

  table.push(
    [
      chalk.bold('install'),
      'Install systemd background service for Deployra daemon & enable boot auto-start',
    ],
    [chalk.bold('start'), 'Start the Deployra daemon background service'],
    [chalk.bold('stop'), 'Stop the Deployra daemon background service'],
    [chalk.bold('restart'), 'Restart the Deployra daemon background service'],
    [
      chalk.bold('status'),
      'Display active status, PID, lingering, and health of Deployra daemon service',
    ],
    [chalk.bold('logs'), 'View Unitup service logs for the Deployra background daemon'],
    [chalk.bold('uninstall'), 'Remove the Deployra daemon background service from systemd'],
  );

  console.log(table.toString());
  console.log(`\n${chalk.yellow('Examples:')}`);
  console.log(
    `  $ ${chalk.green('deployra service install')}   # Install systemd service with boot autostart`,
  );
  console.log(`  $ ${chalk.green('deployra service start')}     # Start daemon service`);
  console.log(`  $ ${chalk.green('deployra service status')}    # View service status & lingering`);
  console.log(`  $ ${chalk.green('deployra service logs')}      # View daemon service logs`);
  console.log(`  $ ${chalk.green('deployra service restart')}   # Restart daemon service\n`);
}

async function tryEnableLinger(): Promise<{ enabled: boolean; note?: string }> {
  if (process.platform !== 'linux') {
    return { enabled: true, note: 'Non-Linux platform (simulated / native)' };
  }

  const currentUser = process.env.USER || process.env.LOGNAME;
  if (!currentUser) {
    return { enabled: false, note: 'Could not determine current username' };
  }

  try {
    const check = await safeExec('loginctl', ['show-user', currentUser, '--property=Linger']);
    if (check.stdout.includes('Linger=yes')) {
      return { enabled: true, note: 'Linger is already active' };
    }

    await safeExec('loginctl', ['enable-linger', currentUser]);
    return { enabled: true, note: 'Enabled loginctl user lingering' };
  } catch (err: any) {
    return {
      enabled: false,
      note: `Run 'sudo loginctl enable-linger ${currentUser}' to allow daemon to start on reboot without login. (${err.message})`,
    };
  }
}

export async function serviceCommand(
  action?: 'install' | 'start' | 'stop' | 'restart' | 'status' | 'logs' | 'uninstall',
): Promise<void> {
  if (!action) {
    printServiceHelp();
    return;
  }

  const adapter = new UnitupAdapter();
  const serviceName = 'deployra-daemon';

  try {
    switch (action) {
      case 'install': {
        const rawScript = process.argv[1];
        const scriptPath =
          rawScript && fs.existsSync(rawScript) ? path.resolve(rawScript) : undefined;

        const isRoot = isRootUser();
        const serviceEnv: Record<string, string> = {
          LOG_LEVEL: 'info',
        };
        if (isRoot) {
          serviceEnv.DEPLOYRA_ALLOW_ROOT = 'true';
        }

        const serviceOpts = {
          name: serviceName,
          command: scriptPath ? process.execPath : 'deployra',
          args: scriptPath ? [scriptPath, 'watch'] : ['watch'],
          cwd: process.cwd(),
          restart: 'always' as const,
          force: true,
          logLevelMax: 'info',
          logRateLimitIntervalSec: '10s',
          logRateLimitBurst: 200,
          env: serviceEnv,
        };

        try {
          await createService(serviceOpts);
        } catch (err: any) {
          if (
            process.platform !== 'linux' ||
            err.message?.includes('Failed to reload systemd daemon') ||
            err.message?.includes('systemd is not running')
          ) {
            // Non-fatal if systemd daemon is not actively running (e.g. macOS or container)
          } else {
            throw err;
          }
        }
        console.log(
          chalk.green(`✔ Installed systemd service '${serviceName}' via Unitup (Restart=always).`),
        );

        if (isRoot) {
          console.log(
            chalk.cyan(
              `ℹ Root User Detected: Configured DEPLOYRA_ALLOW_ROOT=true in systemd unit so daemon starts without root privilege errors.`,
            ),
          );
        }

        // Enable user lingering on Linux so service starts automatically on system reboot without SSH login
        const lingerResult = await tryEnableLinger();
        if (lingerResult.enabled) {
          console.log(
            chalk.green(
              `✔ Boot Auto-Start: Systemd user lingering is enabled (service starts on reboot and stays running 24/7).`,
            ),
          );
        } else {
          console.log(chalk.yellow(`⚠ Boot Auto-Start Note: ${lingerResult.note}`));
        }
        break;
      }
      case 'start':
        await adapter.start(serviceName);
        console.log(chalk.green(`✔ Started systemd service '${serviceName}'.`));
        break;
      case 'stop':
        await adapter.stop(serviceName);
        console.log(chalk.green(`✔ Stopped systemd service '${serviceName}'.`));
        break;
      case 'restart':
        await adapter.restart(serviceName);
        console.log(chalk.green(`✔ Restarted systemd service '${serviceName}'.`));
        break;
      case 'status': {
        const res = await adapter.status(serviceName);
        console.log(
          chalk.bold(`Service '${serviceName}': `) +
            (res.active ? chalk.green('Active') : chalk.red('Inactive')) +
            chalk.gray(` (${res.subState || 'unknown'})`),
        );
        if (res.mainPid) {
          console.log(chalk.gray(`  PID: ${res.mainPid}`));
        }

        if (process.platform === 'linux') {
          const lingerCheck = await tryEnableLinger();
          console.log(
            chalk.bold(`Boot Autostart (Linger): `) +
              (lingerCheck.enabled
                ? chalk.green('Enabled')
                : chalk.yellow('Disabled / Needs setup')) +
              chalk.gray(` (${lingerCheck.note})`),
          );
        }
        break;
      }
      case 'logs': {
        const out = await adapter.logs(serviceName, { lines: 100 });
        console.log(chalk.bold(`\n📦 Daemon Service Logs ('${chalk.cyan(serviceName)}'):\n`));
        console.log(typeof out === 'string' ? out : 'No logs found.');
        break;
      }
      case 'uninstall':
        await removeService(serviceName);
        console.log(chalk.green(`✔ Uninstalled systemd service '${serviceName}'.`));
        break;
      default:
        console.log(chalk.red(`✖ Invalid service action '${action}'`));
        printServiceHelp();
        break;
    }
  } catch (err: any) {
    console.error(chalk.red(`✖ Failed to execute service action '${action}': ${err.message}`));
  }
}
